import { twMerge } from 'tailwind-merge';
import {
  Box,
  Typography,
  InputLabel,
  SelectProps,
  InputAdornment,
  TextField as MuiTextField,
} from '@mui/material';

import { Icon } from '../icons';

import { InputProps, InputVariantClassnameMap } from './types';

const variantClassname: InputVariantClassnameMap = {
  outlined: '',
};

export const Input = ({
  label,
  error,
  value,
  pattern,
  onChange,
  required,
  slotProps,
  className,
  placeholder,
  hasSearchIcon,
  type = 'text',
  shrink = true,
  variant = 'outlined',
  ...props
}: InputProps) => {
  const isSelect = !!slotProps?.select;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (pattern) {
      const regex = new RegExp(pattern, 'g');
      const value = event.target.value.replace(regex, '');
      event.target.value = value;
    }
    onChange?.(event);
  };

  const renderSelectValue = (selected: unknown) => {
    const placeholderComponent = (
      <Typography component="span" sx={{ color: 'text.disabled' }}>
        {placeholder || 'Select'}
      </Typography>
    );

    if (Array.isArray(selected)) {
      if (selected.length === 0) {
        return placeholderComponent;
      }
      return selected.join(', ');
    }

    if (typeof selected === 'string') {
      if (selected.length === 0 || selected === '') {
        return placeholderComponent;
      }
      return selected;
    }
    if (!selected || selected === '') {
      return placeholderComponent;
    }

    return selected as string;
  };

  const inputSlotProps: InputProps['slotProps'] = {
    inputLabel: { shrink },
    input: {
      ...slotProps?.input,
      ...(hasSearchIcon
        ? {
            startAdornment: (
              <InputAdornment position="start">
                <Icon.Search className="search-icon" />
              </InputAdornment>
            ),
          }
        : {}),
    },
    ...slotProps,
    ...(slotProps?.select && {
      select: {
        ...slotProps.select,
        displayEmpty: true,
        renderValue: renderSelectValue,
      },
    }),
  };

  const isMultipleSelect = (inputSlotProps?.select as SelectProps)?.multiple;

  return (
    <Box>
      {label && (
        <InputLabel
          error={error}
          className="text-black-primary mb-1 flex items-center"
        >
          <span className="min-w-0 truncate">{label}</span>
          {required && <span className="text-red-primary"> *</span>}
        </InputLabel>
      )}
      <MuiTextField
        type={type}
        error={error}
        variant={variant}
        onChange={handleChange}
        slotProps={inputSlotProps}
        value={isMultipleSelect && !value ? [] : value}
        placeholder={!isSelect ? placeholder : undefined}
        className={twMerge(variantClassname[variant], className)}
        {...props}
      />
    </Box>
  );
};

export type { InputProps };
